import { createApp } from "vue";
import { createPinia } from "pinia";
import { Tooltip } from "bootstrap";
import App from "./App.vue";
import * as Sentry from "@sentry/vue";

import router from "./router";
import ElementPlus from "element-plus";
import i18n from "@/core/plugins/i18n";
import { useAuthStore } from '@/stores/auth';
import { useUserStore } from "./stores/user";
import { storeResetPlugin } from "./stores/reset";

import axios from "axios"
import VueAxios from "vue-axios"

//imports for app initialization
import { initApexCharts } from "@/core/plugins/apexcharts";
import { initInlineSvg } from "@/core/plugins/inline-svg";
import { initVeeValidate } from "@/core/plugins/vee-validate";
import { initKtIcon } from "@/core/plugins/keenthemes";
import { installGlobalExceptionHandler } from "@/core/plugins/global-exception-handler";
import "@/core/plugins/prismjs";
// Code highlighting theme stylesheet, chosen per app theme mode
import "@/core/helpers/code_theme";

import AuthService from "@/core/services/AuthService";
import AuditLogService from "@/core/services/AuditLogService";
import StoragePolicyService from "./core/services/StoragePolicyService";
import StorageBackendService from "./core/services/StorageBackendService";
import FileVersionService from "./core/services/FileVersionService";
import S3ApiService from "./core/services/S3ApiService";
import S3Gateway from "./core/services/S3Gateway";
import MigrationService from "./core/services/MigrationService";
import EdgeSupportService from "./core/services/EdgeSupportService";
import UsageService from "./core/services/UsageService";
import UserService from "./core/services/UserService";
import AdaptiveBucketPolicyService from "./core/services/AdaptiveBucketPolicyService";

const app = createApp(App);

Sentry.init({
  Vue: app,
  dsn: import.meta.env.VITE_APP_SENTRY_DSN,
  integrations: [
    Sentry.browserTracingIntegration({router}),
    Sentry.replayIntegration({
      maskAllText: true,
      blockAllMedia: true
    }),
  ],
  // Performance Monitoring
  tracesSampleRate: 1.0, //  Capture 100% of the transactions
  // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
  tracePropagationTargets: [],
  // Session Replay
  replaysSessionSampleRate: 0.3, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
  replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
  enabled: !!import.meta.env.VITE_APP_SENTRY_DSN
});

const pinia = createPinia();
pinia.use(storeResetPlugin);
app.use(pinia);
app.use(router);
app.use(ElementPlus);
installGlobalExceptionHandler(app);



// 401 interceptor
axios.interceptors.response.use(response => {
  // Any status code that lie within the range of 2xx cause this function to trigger
  return response;
}, error => {
  // Any status codes that falls outside the range of 2xx cause this function to trigger
  if (error.response?.status == 401 && !error.config?.skipAuthLogoutOn401){
    console.log("401 response received, logging out")
    // Remove the auth header, this token won't work any more
    delete axios.defaults.headers.common['Authorization']
    const authstore = useAuthStore();
    authstore.purgeAuth()
    // Remove cached user
    const userstore = useUserStore()
    userstore.delete_cached_user()
    // Redirect to login
    router.push({ name: 'sign-in' });
    // TODO show error?
  }

  return Promise.reject(error);
});

app.use(VueAxios, axios)
// Make the axios instance available via const axios = inject('axios') in components
app.provide('axios', app.config.globalProperties.axios)

// Pass the axios instance to all Services that require the SkyFlok Token
AuthService.init(app.axios)
AuditLogService.init(app.axios)
UserService.init(app.axios)
StoragePolicyService.init(app.axios)
StorageBackendService.init(app.axios)
S3ApiService.init(app.axios)
S3Gateway.init(app.axios)
MigrationService.init(app.axios)
FileVersionService.init(app.axios)
EdgeSupportService.init(app.axios)
UsageService.init(app.axios)
AdaptiveBucketPolicyService.init(app.axios)


initApexCharts(app);
initInlineSvg(app);
initKtIcon(app);
initVeeValidate();

app.use(i18n);

// Longer than Bootstrap's tooltip fade (.15s)
const TOOLTIP_FADE_MS = 300;

type TooltipHost = HTMLElement & { __tooltipCleanup?: () => void };

app.directive("tooltip", {
  // The tooltip must be destroyed before its host element is unloaded from the DOM, otherwise it hangs.
  // Hover shows it, focus only does when it is keyboard focus: focus that a modal returns to the button
  // that opened it should not bring the tooltip back.
  // Clicking a button or link hides it, and it stays hidden until the pointer has left the element
  // (or keyboard focus moved elsewhere), even if the element is still hovered or focused, e.g. after
  // the modal it opened closes under the pointer.
  mounted(el) {
    const host = el as TooltipHost;
    Tooltip.getOrCreateInstance(host, { trigger: "hover" });
    const listeners: [EventTarget, string, EventListener, boolean?][] = [];
    const listen = (target: EventTarget, event: string, listener: EventListener, capture?: boolean) => {
      target.addEventListener(event, listener, capture);
      listeners.push([target, event, listener, capture]);
    };

    let suppressed = false;
    const stopSuppressing = () => {
      suppressed = false;
      document.removeEventListener("pointermove", onPointerMove);
    };
    // The pointer has really left once it moves while the element is no longer under it
    const onPointerMove = () => {
      if (!host.matches(":hover")) stopSuppressing();
    };
    listen(host, "show.bs.tooltip", (event) => {
      if (suppressed) event.preventDefault();
    });
    listen(host, "focusin", () => {
      if (host.matches(":focus-visible")) Tooltip.getInstance(host)?.show();
    });
    listen(host, "focusout", (event) => {
      if (!host.matches(":hover")) Tooltip.getInstance(host)?.hide();
      // Focus moving into a modal the element opened does not count as leaving it
      const next = (event as FocusEvent).relatedTarget as HTMLElement | null;
      if (next && !next.closest(".modal")) stopSuppressing();
    });

    const tagName = host.tagName?.toLowerCase();
    if (tagName === "a" || tagName === "button") {
      // Only hide: disposing here breaks Bootstrap when the tooltip is still fading in
      // (its transition callback then runs on the disposed instance). Unmounting disposes it.
      listen(host, "click", () => {
        Tooltip.getInstance(host)?.hide();
        if (!suppressed) document.addEventListener("pointermove", onPointerMove);
        suppressed = true;
      }, true);
    }

    host.__tooltipCleanup = () => {
      stopSuppressing();
      listeners.forEach(([target, event, listener, capture]) => target.removeEventListener(event, listener, capture));
    };
  },
  // Bootstrap reads the title only once and moves it to data-bs-original-title. When Vue patches a new
  // title onto a reused element (e.g. a table row showing another item), pass it on to the tooltip
  updated(el) {
    const title = (el as HTMLElement).getAttribute("title");
    if (title === null) return;

    el.setAttribute("data-bs-original-title", title);
    el.removeAttribute("title");
    Tooltip.getInstance(el)?.setContent({ ".tooltip-inner": title });
  },
  beforeUnmount(el) {
    const host = el as TooltipHost;
    host.__tooltipCleanup?.();
    delete host.__tooltipCleanup;

    const instance = Tooltip.getInstance(host);
    if (!instance) {
      return;
    }
    instance.hide();
    // Dispose only after the fade-out: Bootstrap's transition callback crashes on a disposed instance
    setTimeout(() => instance.dispose(), TOOLTIP_FADE_MS);
  },
});

app.mount("#app");
